import { Injectable } from '@angular/core';
import { CreatePaymentMethodCardData, loadStripe, Stripe, StripeCardElement, StripeCardNumberElement, StripeElements } from '@stripe/stripe-js';
import { AppSettingsService } from './app-settings.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable()
export class SubscriptionService {
    private stripe: Stripe | null = null;
    private elements: StripeElements | null = null;

    private apiEndpoint;
    private publishableKey;

    constructor(private AppSettings: AppSettingsService, private http: HttpClient) {
        this.apiEndpoint = this.AppSettings.getConfig('API_ENDPOINT');
        this.publishableKey = this.AppSettings.getConfig('STRIPE_PUBLISHABLE_KEY');
        this.initializeStripe();
    }

    private async initializeStripe() {
        this.stripe = await loadStripe(this.publishableKey);
    }

    public getStripeInstance(): Stripe | null {
        return this.stripe;
    }

    getSubscriptionDetails(userName: string): Observable<any> {
        return this.http.get<any>(`${this.apiEndpoint}Subscription/Details/${userName}`);
    }

    processPayment(processPaymentRequest: any): Observable<any> {
        return this.http.post<any>(`${this.apiEndpoint}Subscription/ProcessPayment`, processPaymentRequest, {
            headers: { 'Content-Type': 'application/json' }
        });
    }

    UpdateAccountSettings(subscriptionDetails: any): Observable<any> {
        return this.http.post<any>(`${this.apiEndpoint}Subscription/UpdateSettings`, subscriptionDetails, {
            headers: { 'Content-Type': 'application/json' }
        });
    }

    async createElements() {
        if (!this.elements && this.stripe) {
            this.elements = this.stripe.elements();
        }
        return this.elements;
    }

    async createPaymentToken(cardNumberElement: StripeCardNumberElement) {
        // Ensure Stripe is loaded before proceeding
        if (!this.stripe) {
            this.stripe = await loadStripe(this.publishableKey);
        }
        
        // Generate the token from the cardNumber element
        const { token, error } = await this.stripe.createToken(cardNumberElement);

        if (error) {
            console.error('Token creation error:', error.message);
            return { token: null, error };
        }

        return { token, error: null };
    }

    async createPaymentMethod(cardElement: StripeCardElement): Promise<any> {
        if (!this.stripe) {
            throw new Error('Stripe.js has not been loaded.');
        }   

        const paymentMethodData: CreatePaymentMethodCardData = {
            type: 'card',
            card: cardElement
        };

        return this.stripe.createPaymentMethod(paymentMethodData);
    }

    async createPaymentIntent(productId: string) {
        const response = await fetch(`${this.apiEndpoint}Subscription/CreatePaymentIntent`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                productId: productId
            }),
        });

        const data = await response.json();
        return data.data;
    }

    confirmCardPayment(clientSecret: string, cardElement: any) {
        return this.stripe.confirmCardPayment(clientSecret, {
            payment_method: {
                card: cardElement
            }
        });
    }

    notifyPaymentSuccess(userName: string, paymentIntentId: string) {
        // Call your backend API with payment success details
        return fetch(`${this.apiEndpoint}Subscription/PaymentSuccess`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userName: userName,
                paymentIntentId: paymentIntentId
            })
        }).then(response => {
            if (!response.ok) {
                throw new Error('Failed to notify backend');
            }
            return response.json();
        });
    }

    async getStripeProductDetails(productId: string): Promise<any> {
        const response = await fetch(`${this.apiEndpoint}Subscription/ProductDetails/${productId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        const data = await response.json();
        return data.data;
    }
} 



// WEBPACK FOOTER //
// ./src/app/services/subscription.service.ts